import { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const formatDate = (iso) => new Date(iso).toLocaleString("fr-BE", { dateStyle: "short", timeStyle: "short" });

export function NotificationBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const pollRef = useRef(null);

  const refresh = async () => {
    try {
      const { data } = await api.get("/notifications?limit=10");
      setItems(data.items);
      setUnread(data.unread_count);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    refresh();
    pollRef.current = setInterval(refresh, 30000);
    return () => clearInterval(pollRef.current);
  }, []);

  const markAll = async () => {
    try {
      await api.post("/notifications/read-all");
      refresh();
    } catch { /* ignore */ }
  };

  const openNotif = async (n) => {
    if (!n.read_at) {
      try { await api.patch(`/notifications/${n.id}/read`); } catch { /* ignore */ }
    }
    setOpen(false);
    if (n.payload?.lead_id) nav(`/leads/${n.payload.lead_id}`);
    refresh();
  };

  const label = (n) => {
    if (n.type === "new_lead") return `Nouveau lead : ${n.payload?.name || "?"}`;
    if (n.type === "new_lead_webhook") return `Lead reçu (webhook) : ${n.payload?.name || "?"}`;
    return n.type;
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          className="relative rounded-lg p-2 text-slate-300 hover:bg-slate-800/60"
          data-testid="notification-bell"
          aria-label="Notifications"
        >
          <Bell className="h-4 w-4" />
          {unread > 0 && (
            <span
              className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-[#C4553B] px-1 text-[10px] font-bold text-white tabular"
              data-testid="notification-badge"
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-80 border-slate-700 bg-slate-900 text-slate-100"
      >
        <DropdownMenuLabel className="flex items-center justify-between">
          <span>Notifications</span>
          {unread > 0 && (
            <button
              onClick={markAll}
              className="text-xs text-[#E07A62] hover:underline"
              data-testid="mark-all-read"
            >
              Tout marquer lu
            </button>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-slate-800" />
        {items.length === 0 ? (
          <p className="p-4 text-center text-sm text-slate-500">Aucune notification.</p>
        ) : (
          <ul className="max-h-80 overflow-y-auto">
            {items.map(n => (
              <li key={n.id}>
                <button
                  onClick={() => openNotif(n)}
                  className={`flex w-full flex-col items-start px-3 py-2 text-left hover:bg-slate-800/60 ${n.read_at ? "opacity-60" : ""}`}
                  data-testid={`notif-item-${n.id}`}
                >
                  <div className="text-sm text-slate-100">{label(n)}</div>
                  <div className="text-xs text-slate-500 tabular">{formatDate(n.created_at)}</div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
